import React, { useState, useEffect } from 'react';
import {
  PlusCircle, Trash3Fill, PencilSquare, Search, X, Check,
  ExclamationTriangleFill, ChevronLeft, ChevronRight, Box, PlayCircleFill
} from 'react-bootstrap-icons';
import './ProductsPage.css';
import * as XLSX from "xlsx";
const PRODUCTS_PER_PAGE = 10;
const API = process.env.REACT_APP_API_URL 

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [deleteModal, setDeleteModal] = useState({ open: false, product: null });
  const [currentPage, setCurrentPage] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  // State handles multiple media items (images & videos)
  const [newProduct, setNewProduct] = useState({
    name: '',
    price: '',
    short_description: '',
    full_description: '',
    highlights: '',
    mediaItems: [], // Stores { type: 'image'|'video', url: string, file: File|null, isExisting: boolean }
    rating: 5,
    reviews: 0,
    in_stock: true,
    stock_quantity: 0,
    is_upcoming: false,
    category: ''
  });
const [selectedDomain, setSelectedDomain] = useState('medical');
  // Fetch products
  useEffect(() => {
    fetchProducts();
  }, [selectedDomain]);

  // Filter products logic
  useEffect(() => {
    let result = [...products];
    if (searchTerm) {
      result = result.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (selectedCategory !== 'all') {
      result = result.filter(p => p.category === selectedCategory);
    }
    // Sort new to old
    result.sort((a, b) => (b.product_id || b.id || 0) - (a.product_id || a.id || 0));
    setFilteredProducts(result);
  }, [products, searchTerm, selectedCategory]);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, selectedCategory]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("adminToken");
      if (!token) throw new Error("No authentication token found.");

      const res = await fetch(`${API}/api/admin/products/${selectedDomain}`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (!res.ok) {
        if (res.status === 401) throw new Error("Unauthorized");
        throw new Error('Failed to fetch products');
      }

      const data = await res.json();
      // Ensure ID consistency
      const productsWithId = data.map(p => ({ ...p, id: p.product_id }));
      setProducts(productsWithId);
      setError('');
    } catch (err) {
      console.error("Fetch error:", err);
      setError(err.message);
      showToast(err.message, 'error');
      if (err.message.includes("Unauthorized")) {
        localStorage.removeItem("adminToken");
        // window.location.href = "/"; // Uncomment to auto-redirect
      }
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 4000);
  };

  const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * PRODUCTS_PER_PAGE,
    currentPage * PRODUCTS_PER_PAGE
  );

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  //NEW: Handle Multiple Media Files
  const handleMediaChange = (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const newMedia = files.map(file => ({
        type: file.type.startsWith('video') ? 'video' : 'image',
        url: URL.createObjectURL(file),
        file: file,
        isExisting: false
      }));

      setNewProduct(prev => ({
        ...prev,
        mediaItems: [...prev.mediaItems, ...newMedia]
      }));
    }
  };

  const removeMediaItem = (index) => {
    setNewProduct(prev => {
      const updatedMedia = [...prev.mediaItems];
      const itemToRemove = updatedMedia[index];
      // Revoke object URL to prevent memory leaks for new files
      if (!itemToRemove.isExisting && itemToRemove.url) {
        URL.revokeObjectURL(itemToRemove.url);
      }
      updatedMedia.splice(index, 1);
      return { ...prev, mediaItems: updatedMedia };
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "is_upcoming") {
      setNewProduct(prev => ({
        ...prev,
        is_upcoming: checked,
        ...(checked ? { in_stock: false, stock_quantity: 0 } : {})
      }));
      return;
    }

    setNewProduct(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("adminToken");
    if (!token) return showToast("Authentication required", "error");

    setSubmitting(true)
    const formData = new FormData();
    formData.append("name", newProduct.name);
    formData.append("price", newProduct.price);
    formData.append("short_description", newProduct.short_description);
    formData.append("full_description", newProduct.full_description);
    formData.append("highlights", newProduct.highlights || '');
    formData.append("category", newProduct.category);
    formData.append("rating", newProduct.rating);
    formData.append("reviews", newProduct.reviews);
    // Convert boolean to number/string for backend consistency
    formData.append("in_stock", newProduct.in_stock ? 1 : 0);
    formData.append("stock_quantity", newProduct.stock_quantity);
    formData.append("is_upcoming", newProduct.is_upcoming ? 1 : 0);

    // --- Media Logic ---
    // --- Media Logic ---

    //  New files only
    const newFiles = newProduct.mediaItems
      .filter(m => !m.isExisting)
      .map(m => m.file);

    //  Existing media URLs to keep
    const existingUrls = newProduct.mediaItems
      .filter(m => m.isExisting)
      .map(m => {
        // m.url may be full API URL → convert to relative DB path
        if (m.url.startsWith(API)) {
          return m.url.replace(API, '');
        }
        return m.url;
      });




    //  Append new files
    newFiles.forEach(file => {
      formData.append("media_files", file);
    });

    //  Append existing URLs
    formData.append("existing_media", JSON.stringify(existingUrls));


    const url = editingProduct
      ? `${API}/api/admin/products/${selectedDomain}/${editingProduct.id}`
      : `${API}/api/admin/products/${selectedDomain}`;

    const method = editingProduct ? "PUT" : "POST";

    try {
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });


      const textResponse = await res.text();
      let data;
      try {
        data = JSON.parse(textResponse);
      } catch (err) {
        console.error("Server returned non-JSON:", textResponse);
        throw new Error(`Server Error (${res.status}). Check console.`);
      }

      if (res.ok) {
        showToast(editingProduct ? "Product updated!" : "Product added!");
        closeModal();
        fetchProducts();
      } else {
        showToast(data.message || "Operation failed", "error");
      }
    } catch (err) {
      console.error(err);
      showToast(err.message, "error");
    }finally {
    setSubmitting(false); // <-- stop spinner
  }
  };

  const handleDelete = async () => {
    if (!deleteModal.product) return;

    const id = deleteModal.product.id;
    const token = localStorage.getItem("adminToken");

    try {
      let url = `${API}/api/admin/products/${selectedDomain}/${id}`;
      let method = "DELETE";


      if (deleteModal.permanent) {
        url += "?permanent=true";
      }

      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        if (deleteModal.permanent) {
          // Removing  completely from frontend list
          setProducts(prev => prev.filter(p => p.id !== id));
          showToast("Product permanently deleted");
        } else {
          // Soft delete 
          setProducts(prev =>
            prev.map(p =>
              p.id === id ? { ...p, is_active: 0 } : p
            )
          );
          showToast("Product hidden (can be restored later)");
        }
      } else {
        showToast("Action failed", "error");
      }
    } catch (err) {
      showToast("Network error", "error");
    }

    setDeleteModal({ open: false, product: null, permanent: false });
  };



  const openEditModal = async (productId) => {
    try {
      const token = localStorage.getItem("adminToken");
      if (!token) throw new Error("Not authenticated");

      const res = await fetch(`${API}/api/admin/products/${selectedDomain}/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data.message || "Failed to fetch product");

      // Start with media from product_media table
      let mediaItems = (data.media || [])
        .filter(item => item && item.url && typeof item.url === 'string' && item.url.trim() !== '')
        .map(item => ({
          type: item.type || 'image',
          url: item.url.startsWith('http') ? item.url : `${API}${item.url}`,
          file: null,
          isExisting: true
        }));

      // === KEY FIX: Always add legacy image_url as "existing" if it exists and is NOT already in mediaItems ===
      if (data.image_url && data.image_url.trim() !== '') {
        const legacyUrl = data.image_url.startsWith('http')
          ? data.image_url
          : `${API}${data.image_url}`;

        // Avoid duplicate if it's already in product_media
        const isAlreadyIncluded = mediaItems.some(item => item.url === legacyUrl);
        if (!isAlreadyIncluded) {
          mediaItems.unshift({  // Put it first (as main image)
            type: 'image',
            url: legacyUrl,
            file: null,
            isExisting: true
          });
        }
      }

      setNewProduct({
        name: data.name || '',
        price: data.price || '',
        short_description: data.short_description || '',
        full_description: data.full_description || '',
        highlights: data.highlights || '',
        category: data.category || '',
        rating: data.rating || 5,
        reviews: data.reviews || 0,
        in_stock: data.in_stock === 1,
        stock_quantity: data.stock_quantity || 0,
        is_upcoming: data.is_upcoming === 1,
        mediaItems: mediaItems  // Now includes legacy image safely
      });

      setEditingProduct({
        ...data,
        id: data.product_id
      });

      setIsModalOpen(true);

    } catch (err) {
      console.error("Edit fetch error:", err);
      showToast(err.message || "Failed to load product for editing", "error");
    }
  };


  const closeModal = () => {
    // Cleanup URLs
    newProduct.mediaItems.forEach(item => {
      if (!item.isExisting && item.url) URL.revokeObjectURL(item.url);
    });

    setNewProduct({
      name: '', price: '', short_description: '', full_description: '',
      mediaItems: [], rating: 5, reviews: 0, in_stock: true, category: '', stock_quantity: 0, is_upcoming: false
    });
    setEditingProduct(null);
    setIsModalOpen(false);
  };

  const categories = [...new Set(products.map(p => p.category))];
  const publishProduct = async (id) => {
    const token = localStorage.getItem("adminToken");
    await fetch(`${API}/api/admin/products/${selectedDomain}/${id}/publish`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` }
    });
    fetchProducts();
  };

  const unpublishProduct = async (id) => {
    const token = localStorage.getItem("adminToken");
    await fetch(`${API}/api/admin/products/${selectedDomain}/${id}/unpublish`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` }
    });
    fetchProducts();
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const token = localStorage.getItem("adminToken");
    if (!token) return showToast("Authentication required", "error");

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await fetch(`${API}/api/admin/products/${selectedDomain}/bulk-upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData
      });

      const data = await res.json();

      if (res.ok) {
        showToast(`Uploaded ${data.insertedCount} products successfully`);
        fetchProducts();
      } else {
        showToast(data.message || "Bulk upload failed", "error");
      }
    } catch (err) {
      showToast("Upload error", "error");
    }

    e.target.value = ""; // reset input
  };
  const restoreProduct = async (id) => {
    const token = localStorage.getItem("adminToken");
    if (!token) return showToast("Authentication required", "error");

    try {
      const res = await fetch(`${API}/api/admin/products/${selectedDomain}/${id}/restore`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        setProducts(prev =>
          prev.map(p =>
            p.id === id ? { ...p, is_active: 1 } : p
          )
        );
        showToast("Product restored successfully");
      } else {
        showToast("Restore failed", "error");
      }
    } catch (err) {
      showToast("Network error", "error");
    }
  };


  return (
    <div className="admin-dashboard">
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          {toast.type === 'success' ? <Check /> : <ExclamationTriangleFill />}
          {toast.message}
        </div>
      )}

      <div className="dashboard-header">
        <h1 className='text-3xl font-bold text-gray-800 flex items-center gap-3 mt-2'>
          <Box size={30} className="text-rose-600" /> Products List
        </h1>
        <select value={selectedDomain} onChange={(e) => setSelectedDomain(e.target.value)}>
  <option value="medical">Medical</option>
  <option value="food">Food</option>
  <option value="education">Education</option>
  <option value="toy">Toys</option>
  <option value="device">Device</option>
</select>
        <div className='header-buttons'>
        <button className="add-product-btn" onClick={() => { setEditingProduct(null); setIsModalOpen(true); }}>
          <PlusCircle size={22} /> Add New Product
        </button>
        <button className="excel-product-btn" onClick={() => document.getElementById("excelUpload").click()}>
          Bulk Upload (Excel)
        </button>
        <input
          type="file"
          id="excelUpload"
          accept=".xlsx, .xls"
          hidden
          onChange={handleExcelUpload}
        />
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <Search size={20} />
          <input type="text" placeholder="Search products..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className='categories'>
          <option value="all">All Categories</option>
          {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
        </select>
      </div>
      {!loading && filteredProducts.length === 0 && (
  <div className="no-products flex flex-col items-center justify-center py-20">
    <ExclamationTriangleFill size={50} className="text-yellow-600 mb-4" />
    <p className="text-gray-600 text-lg">No products found for "{selectedDomain}"</p>
  </div>
)}
      {!loading && filteredProducts.length > 0 && (
        
        
        <div className="product-table-wrapper">
          
          <div className="table-header">
            <h2>Current Products</h2>
            <span className="results-info">Showing {(currentPage - 1) * PRODUCTS_PER_PAGE + 1}–{Math.min(currentPage * PRODUCTS_PER_PAGE, filteredProducts.length)} of {filteredProducts.length}</span>
          </div>
          <div className="table-responsive">
            <table className="product-table min-w-[900px]">
              <thead>
                <tr>
                  <th>Product ID</th>
                  <th>Image</th>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Price</th>
                  {/* <th>Rating</th> */}
                  <th>Stock</th>
                  <th>Actions</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {paginatedProducts.map(p => (
                  <tr key={p.id}>
                    <td>{p.product_id || p.id}</td>
                    <td>
                      {p.image_url ? (
                        <img
                          src={p.image_url.startsWith('http') ? p.image_url : `${API}${p.image_url}`}
                          alt={p.name}
                          className="product-thumb"
                        />
                      ) : (
                        <div className="no-image">No Image</div>
                      )}
                    </td>
                    <td className="product-name-cell">{p.name}</td>
                    <td>{p.category}</td>
                    <td className="price-cell">₹{parseFloat(p.price).toFixed(2)}</td>
                    {/* <td>⭐ {p.rating || 0}</td> */}
                    <td>
                      {p.is_upcoming ? <span className="stock-badge upcoming">Coming Soon</span> :
                        <span className={`stock-badge ${p.in_stock ? 'in-stock' : 'out-stock'}`}>{p.in_stock ? 'In Stock' : 'Out of Stock'}</span>}
                    </td>
                    <td className="actions">
                      <button onClick={() => openEditModal(p.product_id)} className="icon-btn edit"><PencilSquare size={18} /></button>
                      <button onClick={() => setDeleteModal({ open: true, product: p })} className="icon-btn delete"><Trash3Fill size={18} /></button>
                      {p.status === 'draft' ? (
                        <button
                          className="icon-btn publish-btn"
                          onClick={() => publishProduct(p.id)}
                        >
                          Publish
                        </button>
                      ) : (
                        <button
                          className="icon-btn unpublish-btn"
                          onClick={() => unpublishProduct(p.id)}
                        >
                          Unpublish
                        </button>

                      )}
                      {p.is_active === 0 && (
                        <button
                          className="icon-btn restore-btn"
                          onClick={() => restoreProduct(p.id)}
                          title="Restore Product"
                        >
                          Restore
                        </button>
                      )}


                    </td>
                    <td>
                      {p.is_active === 0 ? (
                        <span className="status-badge archived">HIDDEN</span>
                      ) : (
                        <span className={`status-badge ${p.status}`}>
                          {p.status === 'published' ? 'LIVE' : 'DRAFT'}
                        </span>
                      )}
                    </td>


                  </tr>
                ))}
              </tbody>
            </table>
          </div>
       {/* MOBILE PRODUCT CARDS */}
<div className="product-cards-container hidden-on-desktop">
  {paginatedProducts.map((p) => (
    <div key={p.product_id || p.id} className="product-card">

      {/* HEADER */}
      <div className="card-header">
        {p.image_url ? (
          <img
            src={
              p.image_url.startsWith("http")
                ? p.image_url
                : `${API}${p.image_url}`
            }
            alt={p.name}
            className="card-main-image"
          />
        ) : (
          <div className="card-main-image flex items-center justify-center text-[10px] text-gray-400">
            No Image
          </div>
        )}

        <div className="card-title-area">
          <h3 className="card-title break-words">
            {p.name}
          </h3>

          <div className="card-subtitle break-all">
            {p.category} • ID: {p.product_id || p.id}
          </div>
        </div>
      </div>

      {/* BODY */}
      <div className="card-body">
        <div className="card-row">
          <span className="card-label">Price</span>
          <span className="card-value">
            ₹{parseFloat(p.price).toFixed(2)}
          </span>
        </div>

        <div className="card-row">
          <span className="card-label">Rating</span>
          <span className="card-value">
            ⭐ {p.rating || 0}
          </span>
        </div>

        <div className="card-row">
          <span className="card-label">Stock</span>
          <span>
            {p.is_upcoming ? (
              <span className="stock-badge upcoming">
                Coming Soon
              </span>
            ) : (
              <span
                className={`stock-badge ${
                  p.in_stock ? "in-stock" : "out-stock"
                }`}
              >
                {p.in_stock ? "In Stock" : "Out of Stock"}
              </span>
            )}
          </span>
        </div>

        <div className="card-row">
          <span className="card-label">Status</span>
          <span>
            {p.is_active === 0 ? (
              <span className="status-badge archived">
                HIDDEN
              </span>
            ) : (
              <span className={`status-badge ${p.status}`}>
                {p.status === "published" ? "LIVE" : "DRAFT"}
              </span>
            )}
          </span>
        </div>
      </div>

      {/* ACTIONS */}
      <div className="card-actions">
        <button
          onClick={() => openEditModal(p.product_id || p.id)}
          className="icon-btn edit bg-blue-50 text-blue-700 hover:bg-blue-100 rounded"
        >
          Edit
        </button>

        <button
          onClick={() => setDeleteModal({ open: true, product: p })}
          className="icon-btn delete bg-red-50 text-red-700 hover:bg-red-100 rounded"
        >
          Delete
        </button>

        {p.status === "draft" ? (
          <button
            onClick={() => publishProduct(p.id)}
            className="publish-btn bg-green-50 text-green-700 hover:bg-green-100 rounded text-center"
          >
            Publish
          </button>
        ) : (
          <button
            onClick={() => unpublishProduct(p.id)}
            className="unpublish-btn bg-amber-50 text-amber-800 hover:bg-amber-100 rounded"
          >
            Unpublish
          </button>
        )}

        {p.is_active === 0 && (
          <button
            onClick={() => restoreProduct(p.id)}
            className="restore-btn bg-sky-50 text-sky-700 hover:bg-sky-100 rounded"
          >
            Restore
          </button>
        )}
      </div>

    </div>
  ))}
</div>


          {totalPages > 1 && (
            <div className="pagination-container" style={{ padding: '20px 0', borderTop: '1px solid #e5e7eb' }}>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="text-sm text-gray-600 mr-[500px]">Page {currentPage} of {totalPages}</div>
                <nav className="flex items-center gap-3">
                  <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className="flex items-center gap-2 px-5 py-2.5 rounded-lg border hover:bg-rose-400">Previous</button>
                  <div className="flex items-center justify-center w-12 h-10 rounded-lg bg-rose-600 text-white font-bold">{currentPage}</div>
                  <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} className="flex items-center gap-2 px-5 py-2.5 rounded-lg border hover:bg-rose-400">Next</button>
                </nav>
              </div>
            </div>
          )}
        </div>
      )}

      {/* --- MODAL --- */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
              <button onClick={closeModal} className="close-btn"><X size={40} /></button>
            </div>

            <form onSubmit={handleSubmit} className="product-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Product Name</label>
                  <input name="name" value={newProduct.name} onChange={handleInputChange} required />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <input name="category" value={newProduct.category} onChange={handleInputChange} required />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Price</label>
                  <input name="price" type="number" step="0.01" value={newProduct.price} onChange={handleInputChange} required />
                </div>
                <div className="form-group">
                  <label>Short Description</label>
                  <input name="short_description" value={newProduct.short_description} onChange={handleInputChange} required />
                </div>
              </div>

              <div className="form-group full-width">
                <label>Full Description</label>
                <textarea name="full_description" value={newProduct.full_description} onChange={handleInputChange} rows="5" required />
              </div>
              <div className="form-group full-width">
  <label>Product Key Benefits</label>
  <textarea
    name="highlights"
    value={newProduct.highlights}
    onChange={(e) =>
      setNewProduct(prev => ({
        ...prev,
        highlights: e.target.value
      }))
    }
    rows="3"
    placeholder={`• Fast pain relief
• Reduces fever
• Safe for all ages`}
  />
</div>



              {/* --- MEDIA UPLOAD SECTION --- */}
              <div className="image-upload-section">
                <label className="block mb-2 font-medium">Product Media (Images & Videos)</label>

                <div className="media-preview-grid">
                  {newProduct.mediaItems.map((item, index) => (
                    <div key={index} className="media-preview-item">
                      {item.type === 'video' ? (
                        <div className="video-thumbnail">
                          <video src={item.url} className="w-full h-full object-cover" muted />
                          <PlayCircleFill className="play-icon" size={24} />
                        </div>
                      ) : (
                        <img src={item.url} alt={`Preview ${index}`} className="w-full h-full object-cover" />
                      )}

                      <button
                        type="button"
                        onClick={() => removeMediaItem(index)}
                        className="remove-media-btn"
                        title="Remove"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}

                  <label className="media-upload-box">
                    <input
                      type="file"
                      accept="image/*, video/*"
                      multiple
                      onChange={handleMediaChange}
                      className="hidden-input"
                    />
                    <div className="upload-placeholder">
                      <PlusCircle size={24} />
                      <span>Add Media</span>
                    </div>
                  </label>
                </div>
              </div>

              <label className="checkbox-label mt-4">
                <input type="checkbox" name="is_upcoming" checked={newProduct.is_upcoming} onChange={handleInputChange} />
                <span>Upcoming Product</span>
              </label>

              <div className="stock-row">
                <div className="form-group stock-qty">
                  <label>No. of Stock Items</label>
                  <input type="number" name="stock_quantity" min="0" value={newProduct.stock_quantity} onChange={handleInputChange} disabled={newProduct.is_upcoming} required={!newProduct.is_upcoming} />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={closeModal}>Cancel</button>
                <button type="submit" className="submit-btn" disabled={submitting}>
  {submitting ? (
    <>
      <span className="spinner"></span>
      {editingProduct ? ' Updating...' : ' Adding...'}
    </>
  ) : (
    editingProduct ? 'Update Product' : 'Add Product'
  )}
</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteModal.open && (
        <div className="modal-overlay" onClick={() => setDeleteModal({ open: false, product: null })}>
          <div className="modal-content delete-modal" onClick={e => e.stopPropagation()}>
            <div className="delete-icon-wrapper"><ExclamationTriangleFill size={48} className="delete-icon" /></div>
            <h2>Delete Product?</h2>
            <div className="permanent-delete-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={deleteModal.permanent || false}
                  onChange={(e) =>
                    setDeleteModal(prev => ({ ...prev, permanent: e.target.checked }))
                  }
                />
                Permanently delete <strong>“{deleteModal.product?.name}”</strong>?
              </label>
            </div>
            <div className="delete-actions">
              <button className="cancel-btn" onClick={() => setDeleteModal({ open: false, product: null })}>Cancel</button>
              <button className="delete-confirm-btn" onClick={handleDelete}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsPage;